import { useState, useMemo } from "react"
import { Card, CardBody, Col, Container, Row, Badge, Input, Label, Button, Spinner, Table } from "reactstrap"
import Breadcrumbs from "components/Common/Breadcrumb"
import { formatCurrency } from "utils/formatCurrency"
import { formatReadableDate } from "utils/formatDate"
import { usePettyCashSummary, usePettyCashDailySummary, usePettyCashMonthlySummary } from "hooks/usePettyCashMaster"

const MONTH_NAMES = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"]

const StatCard = ({ title, value, color, icon }) => (
  <Card className="border-0 shadow-sm h-100">
    <CardBody className="d-flex align-items-center gap-3 p-3">
      <div className={`avatar-sm rounded d-flex align-items-center justify-content-center bg-${color} bg-opacity-10 flex-shrink-0`}>
        <i className={`${icon} text-${color} font-size-20`} />
      </div>
      <div className="overflow-hidden">
        <p className="text-muted mb-1 small fw-semibold">{title}</p>
        <h5 className="mb-0 fw-bold">{value}</h5>
      </div>
    </CardBody>
  </Card>
)

const PettyCashReport = () => {
  document.title = "Petty Cash Report"

  const today = new Date().toISOString().slice(0, 10)
  const firstOfMonth = today.slice(0, 8) + "01"
  const [dateFrom, setDateFrom] = useState(firstOfMonth)
  const [dateTo, setDateTo] = useState(today)
  const [applied, setApplied] = useState({ from_date: firstOfMonth, to_date: today })

  const summaryParams = useMemo(() => ({ from_date: applied.from_date, to_date: applied.to_date }), [applied])
  const yearParam = useMemo(() => ({ year: new Date(applied.from_date).getFullYear() }), [applied])

  const { data: summaryRes, isLoading: summaryLoading } = usePettyCashSummary(summaryParams)
  const { data: dailyRes, isLoading: dailyLoading } = usePettyCashDailySummary(summaryParams)
  const { data: monthlyRes, isLoading: monthlyLoading } = usePettyCashMonthlySummary(yearParam)

  const summary = summaryRes?.data
  const daily = dailyRes?.data || []
  const monthly = monthlyRes?.data || []

  const handleApply = () => setApplied({ from_date: dateFrom, to_date: dateTo })

  return (
    <div className="page-content">
      <Container fluid>
        <Breadcrumbs title="Petty Cash" breadcrumbItem="Reports" />

        {/* Date Filter */}
        <Card className="border-0 shadow-sm mb-4">
          <CardBody>
            <Row className="g-3 align-items-end">
              <Col xs={12} sm={4}>
                <Label className="mb-1 small fw-semibold">From Date</Label>
                <Input type="date" bsSize="sm" value={dateFrom} onChange={e => setDateFrom(e.target.value)} />
              </Col>
              <Col xs={12} sm={4}>
                <Label className="mb-1 small fw-semibold">To Date</Label>
                <Input type="date" bsSize="sm" value={dateTo} onChange={e => setDateTo(e.target.value)} />
              </Col>
              <Col xs={12} sm={4}>
                <Button color="primary" size="sm" onClick={handleApply}>Apply</Button>
              </Col>
            </Row>
          </CardBody>
        </Card>

        {/* Summary Cards */}
        {summaryLoading ? (
          <div className="text-center py-4"><Spinner color="primary" /></div>
        ) : (
          <Row className="g-3 mb-4">
            <Col xs={12} sm={4}>
              <StatCard title="Total Cash In" value={formatCurrency(summary?.cashin ?? 0, "BDT")} color="success" icon="mdi mdi-trending-up" />
            </Col>
            <Col xs={12} sm={4}>
              <StatCard title="Total Cash Out" value={formatCurrency(summary?.cashout ?? 0, "BDT")} color="danger" icon="mdi mdi-trending-down" />
            </Col>
            <Col xs={12} sm={4}>
              <StatCard title="Net Balance" value={formatCurrency(summary?.balance ?? 0, "BDT")} color={(summary?.balance ?? 0) >= 0 ? "primary" : "warning"} icon="mdi mdi-bank-outline" />
            </Col>
          </Row>
        )}

        <Row className="g-3">
          {/* Daily Summary */}
          <Col xs={12} lg={6}>
            <Card className="border-0 shadow-sm h-100">
              <CardBody>
                <h6 className="fw-semibold mb-3">Daily Breakdown</h6>
                {dailyLoading ? <Spinner size="sm" color="primary" /> : (
                  <div style={{ maxHeight: 320, overflowY: "auto" }}>
                    <Table size="sm" hover borderless className="mb-0">
                      <thead className="text-muted small">
                        <tr><th>Date</th><th className="text-success">Cash In</th><th className="text-danger">Cash Out</th></tr>
                      </thead>
                      <tbody className="small">
                        {daily.length === 0 ? (
                          <tr><td colSpan={3} className="text-center text-muted">No data for this period.</td></tr>
                        ) : daily.map(row => (
                          <tr key={row.date}>
                            <td>{formatReadableDate(row.date)}</td>
                            <td className="text-success fw-semibold">{formatCurrency(row.total_cashin, "BDT")}</td>
                            <td className="text-danger fw-semibold">{formatCurrency(row.total_cashout, "BDT")}</td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                )}
              </CardBody>
            </Card>
          </Col>

          {/* Monthly Summary */}
          <Col xs={12} lg={6}>
            <Card className="border-0 shadow-sm h-100">
              <CardBody>
                <h6 className="fw-semibold mb-3">Monthly Breakdown ({new Date(applied.from_date).getFullYear()})</h6>
                {monthlyLoading ? <Spinner size="sm" color="primary" /> : (
                  <Table size="sm" hover borderless className="mb-0">
                    <thead className="text-muted small">
                      <tr><th>Month</th><th className="text-success">Cash In</th><th className="text-danger">Cash Out</th></tr>
                    </thead>
                    <tbody className="small">
                      {monthly.length === 0 ? (
                        <tr><td colSpan={3} className="text-center text-muted">No data for this year.</td></tr>
                      ) : monthly.map(row => (
                        <tr key={`${row.year}-${row.month}`}>
                          <td>{MONTH_NAMES[row.month - 1]} {row.year}</td>
                          <td className="text-success fw-semibold">{formatCurrency(row.total_cashin, "BDT")}</td>
                          <td className="text-danger fw-semibold">{formatCurrency(row.total_cashout, "BDT")}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  )
}

export default PettyCashReport
